import { RouterModule, Routes } from '@angular/router';
import { diningAttributesGuard } from '@pages/dining/services/dining-attributes-guard';
import { ErrorPageComponent } from '@shared/components/error/error-page/error-page.component';
import { RegistrationErrorPageComponent } from '@shared/components/error/registration-error-page/registration-error-page.component';
import { SsoLoginFailureComponent } from '@shared/components/sso-login-logout/sso-login-failure/sso-login-failure.component';
import { SsoLogoutComponent } from '@shared/components/sso-login-logout/sso-logout/sso-logout.component';
import { SsoRateLimitationComponent } from '@shared/components/sso-login-logout/sso-rate-limitation/sso-rate-limitation.component';
import { AuthGuard } from '@shared/services/auth-guard.service';
import { RootRouteGuard } from '@shared/services/root-route-guard.service';
import { SiteSetGuard } from '@shared/services/site-set-guard.service';
import { loggedInGuard } from 'core/services/logged-in-guard';
import { switchGuard } from 'core/services/switch-guard';
import { travelSubSwitchGuard } from 'core/services/travel-sub-switch-guard';
import { DINING_SEARCH } from './app.config';

const appRoutes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    canActivate: [RootRouteGuard],
    loadChildren: () => import('./pages/home/home-routes').then((m) => m.homeRoutes),
  },
  {
    path: 'privacy',
    redirectTo: 'product/privacy',
  },
  {
    path: 'terms',
    redirectTo: 'product/terms',
  },
  {
    path: 'accessibility',
    redirectTo: 'product/accessibility',
  },
  {
    path: 'faq',
    redirectTo: 'product/faq',
  },
  {
    path: 'contact',
    redirectTo: 'product/contact',
  },
  {
    path: 'covid',
    redirectTo: 'product/covid',
  },
  {
    path: 'travel-restriction-map',
    redirectTo: 'product/travel-restriction-map',
  },
  {
    path: 'selector',
    loadChildren: () => import('./pages/selector/selector-routes').then((m) => m.selectorRoutes),
    canActivate: [loggedInGuard],
  },
  {
    path: 'landing',
    canActivate: [AuthGuard, diningAttributesGuard],
    loadChildren: () => import('./pages/home/home-routes').then((m) => m.homeRoutes),
  },
  {
    path: 'holding',
    canActivate: [SiteSetGuard],
    loadChildren: () => import('./pages/holding/holding-routes').then((m) => m.holdingRoutes),
  },
  {
    path: 'home',
    canActivate: [SiteSetGuard],
    loadChildren: () => import('./pages/home/home-routes').then((m) => m.homeRoutes),
  },
  {
    path: 'page/:name',
    canActivate: [SiteSetGuard],
    loadChildren: () => import('./pages/product/product.module').then((m) => m.ProductModule),
  },
  {
    path: 'activate/:id',
    canActivate: [SiteSetGuard],
    loadChildren: () => import('./pages/user/user-routes').then((m) => m.userRoutes),
  },
  {
    path: 'forgot/:id',
    canActivate: [SiteSetGuard],
    loadChildren: () => import('./pages/user/user-routes').then((m) => m.userRoutes),
  },
  {
    path: 'user',
    canActivate: [SiteSetGuard],
    loadChildren: () => import('./pages/user/user-routes').then((m) => m.userRoutes),
  },
  {
    path: 'product',
    loadChildren: () => import('./pages/product/product.module').then((m) => m.ProductModule),
  },
  {
    path: 'registration',
    canActivate: [SiteSetGuard],
    loadChildren: () => import('./pages/registration/registration.module').then((m) => m.RegistrationModule),
  },
  {
    path: 'referral',
    loadChildren: () => import('./pages/referral/referral-routes').then((m) => m.referralRoutes),
    canActivate: [SiteSetGuard],
  },
  {
    path: 'booking',
    loadChildren: () => import('./pages/booking/booking-routes').then((m) => m.bookingRoutes),
    canActivate: [AuthGuard, SiteSetGuard],
  },
  {
    path: 'request',
    data: { breadcrumb: '' },
    loadChildren: () => import('./pages/request/request-routes').then((m) => m.requestRoutes),
    canActivate: [AuthGuard, SiteSetGuard],
  },
  {
    path: 'partners',
    data: { breadcrumb: 'Partners', customVariableBreadcrumb: true },
    loadChildren: () => import('./pages/partner/partner-routes').then((m) => m.partnerRoutes),
    canActivate: [AuthGuard, switchGuard('partners_module'), SiteSetGuard],
  },
  {
    path: 'travel',
    data: { breadcrumb: 'Travel' },
    loadChildren: () => import('./pages/travel/travel.module').then((m) => m.TravelModule),
    canActivate: [AuthGuard, switchGuard('travel_module'), travelSubSwitchGuard, SiteSetGuard],
  },
  {
    path: 'dining',
    data: { breadcrumb: 'Dining Search', localSearchKey: DINING_SEARCH },
    loadChildren: () => import('./pages/dining/dining-routes').then((m) => m.diningRoutes),
    canActivate: [AuthGuard, switchGuard('dining_module')],
  },
  {
    path: 'entertainment',
    data: {
      breadcrumb: 'Entertainment search',
      breadcrumbRoute: 'entertainment',
      localSearchKey: 'entertainment-search',
    },
    loadChildren: () => import('./pages/entertainment/entertainment-routes').then((m) => m.entertainmentRoutes),
    canActivate: [AuthGuard, switchGuard('entertainment_module'), SiteSetGuard],
  },
  {
    path: 'preferences',
    data: { breadcrumb: 'YourPreferences' },
    loadChildren: () => import('./pages/preferences/preferences-routes').then((m) => m.preferencesRoutes),
    canActivate: [AuthGuard, switchGuard('preferences_page'), SiteSetGuard],
  },
  {
    path: 'member',
    data: { breadcrumb: 'Profile' },
    loadChildren: () => import('./pages/member/member.module').then((m) => m.MemberModule),
    canActivate: [AuthGuard, SiteSetGuard],
  },
  {
    path: 'member-events',
    data: {
      breadcrumb: 'Member events search',
      breadcrumbRoute: 'member-events',
      localSearchKey: 'member-events-search',
    },
    loadChildren: () => import('./pages/member-events/member-events-routes').then((m) => m.memberEventsRoutes),
    canActivate: [AuthGuard, switchGuard('member_events_module'), SiteSetGuard],
  },
  {
    path: 'member-benefits',
    data: { breadcrumb: 'Offers search', breadcrumbRoute: 'member-benefits', localSearchKey: 'member-benefits-search' },
    loadChildren: () => import('./pages/member-benefits/member-benefits-routes').then((m) => m.memberBenefitsRoutes),
    canActivate: [AuthGuard, switchGuard('member_benefits_module'), SiteSetGuard],
  },
  {
    path: 'inspiration',
    data: { breadcrumb: 'Inspiration' },
    loadChildren: () => import('./pages/inspiration/inspiration-routes').then((m) => m.inspirationRoutes),
    canActivate: [AuthGuard, switchGuard('inspiration_module'), SiteSetGuard],
  },
  {
    path: 'benefits',
    data: { breadcrumb: 'Centurion Benefits', breadcrumbRoute: 'benefits' },
    loadChildren: () => import('./pages/benefits/benefits-routes').then((m) => m.benefitsRoutes),
    canActivate: [AuthGuard, switchGuard('enable_centurion_benefits'), SiteSetGuard],
  },
  {
    path: 'request-management',
    data: { breadcrumb: 'Request Management', breadcrumbRoute: 'request-management' },
    loadChildren: () =>
      import('./pages/request-management/request-management-routes').then((m) => m.requestManagementRoutes),
    canActivate: [AuthGuard, switchGuard('request_management_feature'), SiteSetGuard],
  },
  {
    path: 'error/login-failure',
    component: SsoLoginFailureComponent,
  },
  {
    path: 'logout',
    component: SsoLogoutComponent,
  },
  {
    path: 'error/rate-limitation',
    component: SsoRateLimitationComponent,
  },
  {
    path: 'error/not-found',
    data: { breadcrumb: 'Error' },
    component: ErrorPageComponent,
  },
  {
    path: 'error/registration',
    component: RegistrationErrorPageComponent,
  },
  {
    path: '**',
    redirectTo: '/error/not-found',
  },
];

export const AppRoutingModule = RouterModule.forRoot(appRoutes, {
  enableTracing: false,
  anchorScrolling: 'enabled',
});
